import React from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import FieldInput from "../global/form/FieldInput";
import { Formik } from "formik";
import { ReactSortable } from "react-sortablejs";
import { isFeatureEnabled } from "../global/GlobalTools";

// Per-class row height (px) used to keep the editor column aligned with the
// preview column. Only applied under the partial_averages_in_development_areas
// feature flag; otherwise the original heights are kept untouched.
const PARTIAL_AVG_ROW_HEIGHT = 64;
const DEFAULT_DEV_AREA_ROW_HEIGHT = 55;
const DEFAULT_DEV_AREA_CLASS_HEIGHT = 50;

//Custom styles
const customStyles = {
  elementsItem: {
    border: "solid 1px #cccccc",
    borderRadius: "8px",
  },
  moveIconInput: {
    cursor: "n-resize",
    marginTop: "18px",
    display: "block",
  },
  pushButtonIcons: {
    cursor: "n-resize",
  },
  noPaddingIcons: {
    padding: "0px",
  },
  removeElement: {
    fontSize: "13px",
    padding: "4px", //remove button
    position: "relative",
    top: "-15px",
    cursor: "pointer",
  },
  removeElementAverage: {
    fontSize: "15px",
    marginTop: "-63px",
    cursor: "pointer",
    float: "right",
  },
  subjectRemoveIcon: {
    fontSize: "15px",
    marginTop: "-70px",
    cursor: "pointer",
    float: "right",
  },
  subjectRemoveIconDevelopmentArea: {
    fontSize: "15px",
    marginTop: "-53px",
    cursor: "pointer",
    float: "right",
  },
  blankLineRemoveIcon: {
    fontSize: "15px",
    marginTop: "-38px",
    cursor: "pointer",
    float: "right",
  },
  verticalText: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    cursor: "n-resize",
    textAlign: "center",
    // truncate text after two lines with three dots
    overflow: "hidden",
    // textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
};

/**
 * Converts base items to interactive elements for template building
 * @Props {Obj} item  - Element of some kind to be added to the template
 * @Props {Int} index - Position of this item in the list
 * @props {func} getOptions - optienes los opciones del AsyncSelect
 * @props {array} defaulsOptions - contienes las opciones por defecto para el AsyncSelect
 * @props {func} selectClass - almacena el valor de la opcion seleccionada
 * @returns Block element.
 */
export default function ReportCardItemCreator(props) {
  const [t] = useTranslation(["reportCards"]);
  let designItem = false;
  let item = props.item;
  let index = props.position;
  const commentView = props.commentView;
  switch (item.type) {
    case "phantomElement":
      // elemente that keeps the sortable Js component even if all items have been removed.
      designItem = <Col md={12} key={index} id={index}></Col>;
      break;
    case "subject":
      designItem = (
        <Col
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          xxl={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className="mb-3 p-1"
        >
          <Row className="mx-0">
            {item.selectionMode ? (
              <Col
                xs={10}
                sm={10}
                md={10}
                lg={10}
                xl={10}
                xxl={10}
                className="px-0"
              >
                <Form.Check
                  id={"averagingCheckBox-" + index}
                  name="averaging"
                  type="checkbox"
                  checked={item.averaging}
                  onChange={() => {
                    props.selectClassesForAveraging(
                      item && item.value && item.value.value
                        ? item.value.value
                        : null,
                      index
                    );
                  }}
                  label={item && item.value ? item.value.label : ""}
                />
              </Col>
            ) : (
              <Col
                xs={10}
                sm={10}
                md={10}
                lg={10}
                xl={10}
                xxl={10}
                className="px-0"
              >
                {t("reportCards:draggableElements.subject")}
                <AsyncSelect
                  id={"subjectSelect-" + index}
                  loadOptions={props.getOptions}
                  defaultOptions={props.defaulsOptions}
                  onChange={(item) => props.selectClass(item, index)}
                  value={item.value ? item.value : null}
                  isDisabled={item.disabled}
                  formatOptionLabel={function (data, meta) {
                    let label = data.label;
                    if (meta.context === "menu" && data.isDisabled) {
                      label = label + data.subjectLabel;
                    }
                    return <span dangerouslySetInnerHTML={{ __html: label }} />;
                  }}
                />
              </Col>
            )}
            <Col
              xs={2}
              sm={2}
              md={2}
              lg={2}
              xl={2}
              xxl={2}
              className="px-0 pt-2 text-center"
              style={{ height: "56px" }}
            >
              <i
                id={"bi-list-" + index}
                style={customStyles.moveIconInput}
                className="bi bi-list "
              ></i>
            </Col>
          </Row>
          {!item.disabled && (
            <i
              id={"removeElement-" + index}
              style={customStyles.subjectRemoveIcon}
              className="bi bi-x-circle-fill text-danger"
              onClick={() => props.removeItemByButton(item, index)}
            ></i>
          )}
        </Col>
      );
      break;
    case "blankLine":
      designItem = (
        <Col
          md={12}
          style={customStyles.elementsItem}
          id={index}
          key={index}
          className={commentView ? "hide" : "mb-3 p-1"}
        >
          <Row>
            <Col md={10} className="opacity-25">
              {t("reportCards:draggableElements.blankLine")}
            </Col>
            <Col md={1}>
              <i
                id={"bi-list-" + index}
                style={customStyles.pushButtonIcons}
                className="bi bi-list float-end"
              ></i>
            </Col>
          </Row>
          {!item.disabled && (
            <i
              id={"removeElement-" + index}
              style={customStyles.blankLineRemoveIcon}
              className="bi bi-x-circle-fill text-danger"
              onClick={() => props.removeItemByButton(item, index)}
            ></i>
          )}
        </Col>
      );
      break;
    case "dottedLine":
      designItem = (
        <Col
          md={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className={commentView ? "hide" : "mb-3 p-1"}
        >
          <Row>
            <Col md={10}>- - - - - - - - - - - </Col>
            <Col md={1}>
              <i
                id={"bi-list-" + index}
                style={customStyles.pushButtonIcons}
                className="bi bi-list float-end"
              ></i>
            </Col>
          </Row>
          {!item.disabled && (
            <i
              id={"removeElement-" + index}
              style={customStyles.blankLineRemoveIcon}
              className="bi bi-x-circle-fill text-danger"
              onClick={() => props.removeItemByButton(item, index)}
            ></i>
          )}
        </Col>
      );
      break;
    case "average":
      designItem = (
        <Col
          md={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className={commentView ? "hide" : "mb-3 p-1"}
        >
          <Row>
            <Col md={9}>
              {t("reportCards:draggableElements.average")}
              <Formik initialValues={item.values}>
                {(values) => (
                  <Row>
                    <Col lg={12} style={{ maxHeight: "30px" }}>
                      <FieldInput
                        id={"averageNameInput-" + index}
                        name="name"
                        type="text"
                        label={""}
                        placeholder=""
                        onBlur={() => {
                          item.values.name = values.values.name;
                        }}
                        value={values.values.name}
                        style={{
                          padding: "0px 10px 0px 10px",
                        }}
                        className={""} //set to false to remove the bottom margin
                        disabled={item.disabled}
                      />
                    </Col>
                  </Row>
                )}
              </Formik>
            </Col>

            {item.selectionMode ? (
              <Col md={1} style={customStyles.noPaddingIcons}>
                <i
                  id={"averageConfirmIcon-" + index}
                  style={{ cursor: "pointer", marginTop: "18px" }}
                  className="bi bi-check-circle-fill float-end me-1 text-danger"
                  onClick={() => props.changeClassesSelectionMode(false, index)}
                ></i>
              </Col>
            ) : (
              <Col md={1} style={customStyles.noPaddingIcons}>
                <i
                  id={"averageSettingsIcon-" + index}
                  style={
                    item.disabled
                      ? { display: "none" }
                      : {
                          cursor: "pointer",
                          display: "block",
                          marginTop: "18px",
                        }
                  }
                  className="bi bi-gear-fill float-end me-1 text-danger"
                  onClick={() => props.editAveragingFormat(item.values, index)}
                  disabled={item.disabled}
                ></i>
              </Col>
            )}

            <Col md={1} style={{ ...customStyles.noPaddingIcons }}>
              <i
                id={"bi-list-" + index}
                style={customStyles.moveIconInput}
                className="bi bi-list"
              ></i>
            </Col>
          </Row>
          {!item.disabled && (
            <i
              id={"removeElement-" + index}
              style={customStyles.removeElementAverage}
              className="bi bi-x-circle-fill text-danger"
              onClick={() => props.removeItemByButton(item, index)}
            ></i>
          )}
        </Col>
      );
      break;
    case "absences":
      designItem = (
        <Col
          md={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className={commentView ? "hide" : "mb-3 p-1"}
        >
          <Row>
            <Col md={10}>{t("reportCards:draggableElements.assistance")}</Col>
            <Col md={1}>
              <i
                id={"bi-list-" + index}
                style={customStyles.pushButtonIcons}
                className="bi bi-list float-end"
              ></i>
            </Col>
          </Row>
          {!item.disabled && (
            <i
              id={"removeElement-" + index}
              style={customStyles.blankLineRemoveIcon}
              className="bi bi-x-circle-fill text-danger"
              onClick={() => props.removeItemByButton(item, index)}
            ></i>
          )}
        </Col>
      );
      break;
    case "groupAverage":
      designItem = (
        <Col
          md={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className={commentView ? "hide" : "mb-3 p-1"}
        >
          <Row>
            <Col md={10}>{t("reportCards:draggableElements.groupAverage")}</Col>
            <Col md={1}>
              <i
                id={"bi-list-" + index}
                style={customStyles.pushButtonIcons}
                className="bi bi-list float-end"
              ></i>
            </Col>
          </Row>
          {!item.disabled && (
            <i
              id={"removeElement-" + index}
              style={customStyles.blankLineRemoveIcon}
              className="bi bi-x-circle-fill text-danger"
              onClick={() => props.removeItemByButton(item, index)}
            ></i>
          )}
        </Col>
      );
      break;
    case "developmentArea":
      {
        item.classes = item.classes || item.subjects;
        for (let element of item.classes) {
          if (!element.value && element.values) {
            element.value = {
              value: element.values.id,
              label: element.values.name,
              developmentAreaID: element.values.developmentAreaID,
              developmentAreaName: element.values.developmentAreaName,
              title: element.values.developmentAreaName,
            };
          }
        }
        // elemente that keeps the sortable Js component even if all items have been removed.
        // Under the FF, use a per-class height of 64px so this column lines up with
        // the preview column (which renders each class at 64px). Off → original 55.
        const devAreaRowHeight = isFeatureEnabled(
          "partial_averages_in_development_areas"
        )
          ? PARTIAL_AVG_ROW_HEIGHT
          : DEFAULT_DEV_AREA_ROW_HEIGHT;
        let heightRow = item.classes?.length * devAreaRowHeight;
        let heighTitle = item.classes?.length * devAreaRowHeight;
        // CF title column. Under the FF: show the full name (no hard character
        // truncation) — it fills the available height, is clamped by CSS, gets
        // more vertical lines for longer names, is vertically centered, and keeps
        // the complete name in a tooltip so it is never lost. Off → original
        // character-based truncation, untouched.
        const partialAveragesFF = isFeatureEnabled(
          "partial_averages_in_development_areas"
        );
        let titleColumn;
        if (partialAveragesFF) {
          const fullTitle = "" + item.title;
          const titleLineClamp = fullTitle.length > 22 ? 3 : 2;
          titleColumn = (
            <Col
              md={3}
              style={{ maxHeight: heighTitle, cursor: "n-resize" }}
              className="bi-list d-flex align-items-center justify-content-center"
              title={fullTitle}
            >
              <span
                style={{
                  ...customStyles.verticalText,
                  maxHeight: heighTitle,
                  WebkitLineClamp: titleLineClamp,
                }}
              >
                {fullTitle}
              </span>
            </Col>
          );
        } else {
          item.titlem = "" + item.title;
          const charactersByElement = 15;
          const maxlengtTitle = item.classes?.length * charactersByElement;
          const title =
            item.titlem.length > maxlengtTitle
              ? item.titlem.substring(0, maxlengtTitle) + "..."
              : item.titlem;
          titleColumn = (
            <Col
              md={3}
              style={{ ...customStyles.verticalText, maxHeight: heighTitle }}
              className="bi-list"
            >
              {title}
            </Col>
          );
        }
        const listFormatItems = props.listFormatItems || [];
        designItem = (
          <Col
            md={12}
            style={customStyles.elementsItem}
            key={index}
            id={index}
            className="mb-3 p-1"
          >
            <Row style={{ maxHeight: heightRow }}>
              {titleColumn}
              <Col md={9} style={{ paddingLeft: "0" }}>
                {listFormatItems.length > 0 && (
                  <ReactSortable
                    id={"developmentAreaSortable-" + index}
                    list={item.classes} // List of items
                    setList={(newList) => {
                      const newItems = [...listFormatItems];
                      // Filter the item that is subject type
                      let newClassesList = newList.filter(
                        (item) => item.type === "subject"
                      );
                      // Get the class that does not have developmentAreaID
                      const classWithoutGroup = newClassesList.find(
                        (classList) =>
                          classList.value && !classList.value.developmentAreaID
                      );
                      // Filter classes that have the same development area
                      newClassesList = newClassesList.filter(
                        (classList) =>
                          classList.value?.developmentAreaID ===
                            item.development_area_id || !classList.value
                      );
                      const previousClassCount = item.classes.length;
                      newItems[index].classes = newClassesList;
                      if (classWithoutGroup) {
                        newItems.splice(index + 1, 0, classWithoutGroup);
                      }
                      const cfHasSavedWeightsForDrop = !!(
                        props.cfWeights &&
                        props.cfWeights[item.development_area_id] &&
                        Object.keys(props.cfWeights[item.development_area_id])
                          .length
                      );
                      const classWasAdded =
                        newClassesList.length > previousClassCount;
                      if (
                        classWasAdded &&
                        cfHasSavedWeightsForDrop &&
                        props.requestAddClassToWeightedCF
                      ) {
                        props.requestAddClassToWeightedCF({
                          executeUpdate: () =>
                            props.updateFormatList(newItems),
                        });
                      } else {
                        props.updateFormatList(newItems);
                      }
                    }}
                    // Set the group values to only receive class type elements.
                    group={{ name: "options", pull: false, put: true }}
                  >
                    {item.classes.map((classItem, indexClass) => {
                      // 56px + mb-2 (8px) = 64px pitch under the FF, matching the
                      // preview column. Off → original 50px.
                      const classHeight = isFeatureEnabled(
                        "partial_averages_in_development_areas"
                      )
                        ? "56px"
                        : `${DEFAULT_DEV_AREA_CLASS_HEIGHT}px`;
                      // True once this CF has saved weights (the modal persisted % for
                      // at least one class). Used both to switch to the weighted layout
                      // and to require the delete confirmation.
                      const cfHasSavedWeights = !!(
                        props.cfWeights &&
                        props.cfWeights[item.development_area_id] &&
                        Object.keys(props.cfWeights[item.development_area_id])
                          .length
                      );
                      // Per-class weight (%) shown OUTSIDE the bordered box, to its
                      // right (per design). Display-only: reads from cfWeights, never
                      // mutates the class label/format. Gated by the FF + the "Class
                      // Weights" switch; the weighted layout (and its reserved % space)
                      // only kicks in once the CF already has saved weights, so turning
                      // the switch on does not reserve empty space — the % shows live
                      // after saving. Off → original markup, untouched.
                      const showWeight =
                        isFeatureEnabled(
                          "partial_averages_in_development_areas"
                        ) &&
                        props.showCFWeights &&
                        cfHasSavedWeights;
                      const weight =
                        showWeight &&
                        classItem.value &&
                        props.cfWeights &&
                        props.cfWeights[item.development_area_id]
                          ? props.cfWeights[item.development_area_id][
                              String(classItem.value.value)
                            ]
                          : undefined;
                      // Removes this class from its CF (and the CF if it becomes empty).
                      const removeClass = () => {
                        const newItems = [...props.listFormatItems];
                        newItems[index].classes.splice(indexClass, 1);
                        // Remove the group if it has no elements
                        if (newItems[index].classes.length === 0)
                          newItems.splice(index, 1);
                        // Update the list of items
                        props.updateFormatList(newItems);
                        props.refreshAvailableClassList();
                      };
                      // Deleting a class from a CF that already has saved weights must
                      // confirm first (it removes the whole CF weighting). If the CF has
                      // no saved weights yet, remove directly.
                      const onRemoveWeightedClass = () => {
                        if (cfHasSavedWeights && props.requestDeleteWeightedClass) {
                          props.requestDeleteWeightedClass({
                            developmentAreaId: item.development_area_id,
                            executeRemoval: removeClass,
                          });
                        } else {
                          removeClass();
                        }
                      };
                      // The class selector (shared by both layouts below).
                      const classSelect = item.selectionMode ? (
                        <Form.Check
                          id={"averagingCheckBox-" + index + "-" + indexClass}
                          name="averaging"
                          type="checkbox"
                          checked={classItem.averaging}
                          onChange={() => {
                            props.selectClassesForAveraging(
                              classItem &&
                                classItem.value &&
                                classItem.value.value
                                ? classItem.value.value
                                : null,
                              index,
                              indexClass
                            );
                          }}
                          label={
                            classItem && classItem.value
                              ? classItem.value.label
                              : ""
                          }
                        />
                      ) : (
                        <AsyncSelect
                          id={"subjectSelect-" + index + "-" + indexClass}
                          loadOptions={props.getOptions}
                          defaultOptions={props.defaulsOptions?.[0]?.options.filter(
                            (subject) =>
                              subject?.developmentAreaID ==
                              item.development_area_id // Filter the options by the development area id
                          )}
                          onChange={(classItem) =>
                            props.selectClass(classItem, index, indexClass)
                          }
                          value={
                            classItem?.value || classItem?.values || null
                          }
                          isDisabled={classItem.disabled}
                          formatOptionLabel={function (data) {
                            return (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: data.label,
                                }}
                              />
                            );
                          }}
                        />
                      );
                      // Original layout (kept untouched when the FF/switch are off).
                      const classBody = (
                        <>
                          <Row>
                            <Col md={10}>{classSelect}</Col>
                            <Col md={1}>
                              <i
                                id={"bi-list-class-" + index + "-" + indexClass}
                                style={customStyles.pushButtonIcons}
                                className="bi bi-list float-end mt-1"
                              ></i>
                            </Col>
                          </Row>
                          {!classItem.disabled && (
                            <i
                              id={"removeElement-class-" + index + "-" + indexClass}
                              style={
                                customStyles.subjectRemoveIconDevelopmentArea
                              }
                              className="bi bi-x-circle-fill text-danger"
                              onClick={removeClass}
                            />
                          )}
                        </>
                      );
                      // Weight layout: flexbox (no Bootstrap grid gutters that would
                      // overflow the narrower box). Select grows on the left; on the
                      // right, the remove (X) sits on top and the drag handle right
                      // below it, in a fixed-width column so they stay aligned across
                      // all rows regardless of the % text length.
                      const classControls = (
                        <div className="d-flex align-items-center h-100">
                          <div
                            className="flex-grow-1"
                            style={{ minWidth: 0 }}
                          >
                            {classSelect}
                          </div>
                          <div
                            className="d-flex flex-column align-items-center justify-content-center ms-1 flex-shrink-0"
                            style={{ width: "20px" }}
                          >
                            {!classItem.disabled && (
                              <i
                                id={"removeElement-class-" + index + "-" + indexClass}
                                className="bi bi-x-circle-fill text-danger"
                                style={{
                                  fontSize: "15px",
                                  cursor: "pointer",
                                  lineHeight: 1,
                                }}
                                onClick={onRemoveWeightedClass}
                              />
                            )}
                            <i
                              id={"bi-list-class-" + index + "-" + indexClass}
                              className="bi bi-list mt-1"
                              style={{ cursor: "n-resize", lineHeight: 1 }}
                            ></i>
                          </div>
                        </div>
                      );
                      // With the FF + switch on: bordered box on the left, weight %
                      // as a sibling to its right (outside the border), vertically
                      // centered at the class height.
                      return showWeight ? (
                        // mb-2 only (no p-1): the inner bordered box keeps the p-1,
                        // so the per-class pitch stays at classHeight + mb-2 = 64px
                        // and the cards do not overflow the CF container.
                        <Col
                          md={12}
                          key={indexClass}
                          id={"classItem-" + index + "-" + indexClass}
                          className="mb-2"
                          style={{ height: classHeight }}
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{ height: "100%" }}
                          >
                            <div
                              style={{
                                ...customStyles.elementsItem,
                                height: "100%",
                                // Allow the box to shrink to the available width
                                // so the card + % stay inside the CF container
                                // (the select's min-width would otherwise overflow).
                                minWidth: 0,
                              }}
                              className="flex-grow-1 p-1"
                            >
                              {classControls}
                            </div>
                            <div
                              id={"classWeight-" + index + "-" + indexClass}
                              className="flex-shrink-0 text-center"
                              // Fixed width so every bordered box ends up the same
                              // size regardless of the % text length (25% vs 33.34%);
                              // this keeps the drag handle aligned across all rows.
                              style={{ width: "60px", whiteSpace: "nowrap" }}
                            >
                              {weight != null ? (
                                <span
                                  id={
                                    "classWeightValue-" + index + "-" + indexClass
                                  }
                                >{`${weight}%`}</span>
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                        </Col>
                      ) : (
                        <Col
                          md={12}
                          style={{
                            ...customStyles.elementsItem,
                            height: classHeight,
                          }}
                          key={indexClass}
                          id={"classItem-" + index + "-" + indexClass}
                          className="mb-2 p-1"
                        >
                          {classBody}
                        </Col>
                      );
                    })}
                  </ReactSortable>
                )}
              </Col>
            </Row>
          </Col>
        );
      }
      break;
    case "main_subject":
      designItem = (
        <Col
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          xxl={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className="mb-3 p-1"
        >
          <Row className="mx-0">
            <Col
              xs={10}
              sm={10}
              md={10}
              lg={10}
              xl={10}
              xxl={10}
              className="px-0"
            >
              {t("reportCards:draggableElements.main_subject")}
              <AsyncSelect
                id={"mainSubjectSelect-" + index}
                loadOptions={props.getSubjects}
                defaultOptions={props.defaulsSubjects}
                onChange={(item) => props.selectSubjects(item, index)}
                value={item.value ? item.value : null}
                isDisabled={item.disabled}
                formatOptionLabel={function (data) {
                  return (
                    <span dangerouslySetInnerHTML={{ __html: data.label }} />
                  );
                }}
              />
            </Col>
            <Col
              xs={2}
              sm={2}
              md={2}
              lg={2}
              xl={2}
              xxl={2}
              className="px-0 pt-2 text-center"
            >
              <i
                id={"bi-list-" + index}
                style={customStyles.moveIconInput}
                className="bi bi-list"
              ></i>
            </Col>
          </Row>
          <i
            id={"removeElement-" + index}
            style={customStyles.subjectRemoveIcon}
            className="bi bi-x-circle-fill text-danger"
            onClick={() => props.removeItemByButton(item, index)}
          ></i>
        </Col>
      );
      break;
    default:
      designItem = (
        <Col
          md={12}
          style={customStyles.elementsItem}
          key={index}
          id={index}
          className="mb-3 p-1"
        >
          <i
            id={"bi-list-" + index}
            style={customStyles.pushButtonIcons}
            className="pushButtonIcons bi bi-list float-end"
          ></i>
          {t("reportCards:draggableElements.default")}
        </Col>
      );
  }

  return designItem;
}

ReportCardItemCreator.propTypes = {
  getOptions: PropTypes.func,
  editAveragingFormat: PropTypes.func,
  // Per-class weights map { [development_area_id]: { [class_id]: percentage } }
  // and the "Class Weights" switch, used to render the % next to each class.
  cfWeights: PropTypes.object,
  showCFWeights: PropTypes.bool,
  // Opens the confirm modal before deleting a class from a weighted CF.
  requestDeleteWeightedClass: PropTypes.func,
};
